import { z } from 'zod';
import { userRoleSchema } from '@logistics/contracts';

/**
 * The "create a workspace" form. `personName` is asked only the first time —
 * straight after signup, when the account is still called by its mailbox.
 * The API checks all of it again.
 */
export const workspaceFormSchema = z.object({
  personName: z.string().trim().max(80, 'Keep your name under 80 characters.'),
  name: z.string().trim().min(1, 'Name your workspace.').max(60, 'Keep it under 60 characters.'),
});

export type WorkspaceForm = z.input<typeof workspaceFormSchema>;

export const inviteFormSchema = z.object({
  email: z.string().trim().email('Enter a valid email address.'),
  role: userRoleSchema,
});

export type InviteForm = z.input<typeof inviteFormSchema>;

/** The panel opens an invitation from `/invite/<token>` in the emailed link. */
const INVITE_PATH = /^\/invite\/([A-Za-z0-9_-]{8,128})\/?$/;
const INVITE_KEY = 'fleet.invite';

/**
 * Takes the token off the address bar once, at load, and keeps it for this tab
 * until it is used: the person may have to sign in or sign up first. Storage
 * can be unavailable (a private window); then the token lives only in memory.
 */
let remembered: string | undefined;

export function captureInvite(): void {
  const match = INVITE_PATH.exec(window.location.pathname);
  if (match?.[1] === undefined) return;
  remembered = match[1];
  try {
    sessionStorage.setItem(INVITE_KEY, match[1]);
  } catch {
    // Memory only.
  }
  window.history.replaceState(null, '', '/');
}

export function pendingInvite(): string | undefined {
  try {
    return sessionStorage.getItem(INVITE_KEY) ?? remembered;
  } catch {
    return remembered;
  }
}

export function forgetInvite(): void {
  remembered = undefined;
  try {
    sessionStorage.removeItem(INVITE_KEY);
  } catch {
    // Nothing stored.
  }
}

/** The user menu's "New workspace" asks the app to show the create screen. */
export const NEW_WORKSPACE_EVENT = 'fleet:new-workspace';
